import { createClient } from '@supabase/supabase-js';

// Lazy Supabase client creation - waits for env to be loaded
let _supabase: ReturnType<typeof createClient> | null = null;

function getSupabaseClient() {
  if (!_supabase) {
    const env = (window as any).__ENV__;
    if (!env?.SUPABASE_URL || !env?.SUPABASE_ANON_KEY) {
      throw new Error('Missing Supabase config (build-time VITE_* vars — see src/config.ts).');
    }
    _supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
      realtime: { params: { eventsPerSecond: 5 } }
    });
  }
  return _supabase;
}

export interface MatchScore {
  guide_id: string;
  total_score: number;
  breakdown: {
    location: number;
    experience_type: number;
    medicine_comfort: number;
    support_type: number;
    travel: number;
    focus_areas: number;
    specialized_groups: number;
    religious_alignment: number;
    medication_compatibility: number;
    clinical_experience: number;
  };
  reasoning: string[];
  seeker_visible_messages?: string[];
}

export interface MatchResponse {
  seeker_id: string;
  matches: MatchScore[];
  total_guides_evaluated: number;
  timestamp: string;
}

export interface GuideEnhancedProfile {
  guide_id: string;
  about?: string;
  image_url?: string;
  display_name?: string;
}

export interface GuideDetails {
  guide_id: string;
  name: string;
  location?: string;
  bio?: string;
  experience_types?: string[];
  support_types?: string[];
  focus_areas?: string[];
  special_groups?: string[];
  religious_beliefs?: string[];
  backgroundChecked?: boolean | null;
}

export class MatchingService {
  /**
   * Find matches for a seeker using either authenticated session or onboarding data
   */
  static async findMatches(seekerId: string, limit = 5): Promise<MatchResponse> {
    const supabase = getSupabaseClient();
    
    // Get current session for auth
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    // If we have a session, use the normal authenticated flow
    if (session && !sessionError) {
      return this.findMatchesAuthenticated(seekerId, limit, session);
    }
    
    // If no session, try to match using onboarding data from localStorage
    return this.findMatchesFromOnboardingData(seekerId, limit);
  }

  /**
   * Authenticated matching (normal flow)
   */
  private static async findMatchesAuthenticated(seekerId: string, limit: number, session: any): Promise<MatchResponse> {
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase.functions.invoke('match-seeker-guide', {
      body: { seeker_id: seekerId, limit },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) {
      throw new Error(`Matching failed: ${error.message}`);
    }

    return data as MatchResponse;
  }

  /**
   * Preview matching using onboarding data from localStorage (before account creation)
   */
  private static async findMatchesFromOnboardingData(seekerId: string, limit: number): Promise<MatchResponse> {
    console.log('Finding matches from onboarding data for preview...');
    
    // Get all onboarding data from localStorage
    //const onboardingData = this.getOnboardingDataFromStorage();
    
    /*if (!onboardingData) {
      throw new Error('No onboarding data found. Please complete onboarding first.');
    }*/

    // Call the edge function without auth but with onboarding data
    const supabase = getSupabaseClient();
    
    const { data, error } = await supabase.functions.invoke('match-seeker-guide', {
      body: { 
        seeker_id: seekerId, 
        limit,
        //preview_mode: true
      }
    });

    if (error) {
      throw new Error(`Preview matching failed: ${error.message}`);
    }

    return data as MatchResponse;
  }

  /**
   * Get onboarding data from localStorage for preview matching
   */
  private static getOnboardingDataFromStorage() {
    try {
      // Try to get the combined onboarding data
      const keys = [
        'basic_info_data',
        'experience_preferences_data', 
        'medical_and_final_data'
      ];
      
      const combinedData: any = {};
      let hasData = false;
      
      for (const key of keys) {
        const stored = localStorage.getItem(key);
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            Object.assign(combinedData, parsed);
            hasData = true;
          } catch (e) {
            console.warn(`Failed to parse ${key} from localStorage:`, e);
          }
        }
      }
      
      return hasData ? combinedData : null;
    } catch (error) {
      console.error('Error getting onboarding data from storage:', error);
      return null;
    }
  }

  /**
   * Get detailed guide information for display from public profiles
   * Uses display_name if set, otherwise falls back to full_name
   */
  static async getGuideDetails(guideId: string): Promise<GuideDetails> {
    const supabase = getSupabaseClient();

    const [profileResult, basicInfoResult, experienceResult, enhancedProfileResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, full_name, "backgroundChecked"')
        .eq('id', guideId)
        .maybeSingle(),
      supabase
        .from('basic_info')
        .select('location, bio')
        .eq('user_id', guideId)
        .maybeSingle(),
      supabase
        .from('guide_professional_experience')
        .select('experience_types, support_types, focus_areas, special_groups, religious_beliefs')
        .eq('guide_id', guideId)
        .maybeSingle(),
      supabase
        .from('guides_enhanced_profile')
        .select('display_name')
        .eq('guide_id', guideId)
        .maybeSingle()
    ]);

    if (profileResult.error) {
      throw new Error(`Failed to fetch guide profile: ${profileResult.error.message}`);
    }

    const profile = profileResult.data;
    if (!profile) {
      throw new Error('Guide not found in public profiles');
    }

    const basicInfo = basicInfoResult.data ?? null;
    if (basicInfoResult.error) {
      console.warn('Unable to load guide basic info', basicInfoResult.error);
    }

    const experience = experienceResult.data ?? null;
    if (experienceResult.error) {
      console.warn('Unable to load guide professional experience', experienceResult.error);
    }

    const enhancedProfile = enhancedProfileResult.data ?? null;
    if (enhancedProfileResult.error) {
      console.warn('Unable to load guide enhanced profile', enhancedProfileResult.error);
    }

    // Use display_name if set, otherwise fall back to full_name
    const displayName = enhancedProfile?.display_name || profile.full_name || 'Guide';

    return {
      guide_id: profile.id,
      name: displayName,
      location: basicInfo?.location ?? undefined,
      bio: basicInfo?.bio ?? undefined,
      experience_types: experience?.experience_types ?? undefined,
      support_types: experience?.support_types ?? undefined,
      focus_areas: experience?.focus_areas ?? undefined,
      special_groups: experience?.special_groups ?? undefined,
      religious_beliefs: experience?.religious_beliefs ?? undefined,
      backgroundChecked:
        typeof profile.backgroundChecked === 'boolean' ? profile.backgroundChecked : null
    };
  }

  /**
   * Convert matching response guide data to GuideDetails format
   * Use this when you have guide data from the matching function
   */
  static convertMatchDataToGuideDetails(matchGuideData: any): GuideDetails {
    return {
      guide_id: matchGuideData.guide_id,
      name: matchGuideData.name,
      location: matchGuideData.location,
      bio: matchGuideData.bio,
      experience_types: matchGuideData.experience_types,
      support_types: matchGuideData.support_types,
      focus_areas: matchGuideData.focus_areas,
      special_groups: matchGuideData.special_groups,
      religious_beliefs: matchGuideData.religious_beliefs,
    };
  }

  /**
   * Get seeker ID from current session/onboarding - try multiple storage keys
   */
  static getSeekerIdFromSession(): string | null {
    try {
      // First, try direct seeker ID keys (stored as string values)
      const directKeys = [
        'ghost_seeker_id',
        'seeker_id', 
        'seekerId',
        'current_seeker_id'
      ];

      for (const key of directKeys) {
        const directValue = localStorage.getItem(key);
        if (directValue && directValue.trim()) {
          return directValue.trim();
        }
      }

      // Then try JSON object keys where seeker ID might be nested
      const objectKeys = [
        'seeker_onboarding_session',
        'seeker_onboarding_data',
        'seekerOnboardingData', 
        'seeker_session',
        'onboarding_session',
        'current_seeker'
      ];

      for (const key of objectKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            // Check various possible field names for seeker ID
            if (parsed.seeker_id || parsed.seekerId || parsed.id) {
              return parsed.seeker_id || parsed.seekerId || parsed.id;
            }
          } catch (parseError) {
            // Skip this key if JSON parsing fails - might be a string value
            continue;
          }
        }
      }

      // If no stored data found, check URL params as fallback
      const urlParams = new URLSearchParams(window.location.search);
      const seekerIdFromUrl = urlParams.get('seeker_id') || urlParams.get('seekerId');
      if (seekerIdFromUrl) {
        return seekerIdFromUrl;
      }

      return null;
    } catch (error) {
      console.error('Error getting seeker ID from session:', error);
      return null;
    }
  }

  /**
   * Format guide specialty for display
   */
  static formatGuideSpecialty(guide: GuideDetails): string {
    const specialties: string[] = [];
    
    if (guide.focus_areas && guide.focus_areas.length > 0) {
      specialties.push(...guide.focus_areas.slice(0, 2)); // Take first 2 focus areas
    }
    
    if (guide.special_groups && guide.special_groups.length > 0) {
      specialties.push(...guide.special_groups.slice(0, 1)); // Take first special group
    }
    
    if (specialties.length === 0) {
      return 'Psychedelic therapy and integration';
    }
    
    return specialties.join(', ');
  }

  /**
   * Extract location city/state from full location string
   */
  static formatLocation(location?: string): string | null {
    if (!location) return null;

    // Hide raw zip codes — they aren't user-friendly
    if (/^\d{5}(-\d{4})?$/.test(location.trim())) return null;

    // If it looks like "City, ST" or similar, return as-is
    return location.split(',')[0]?.trim() || location;
  }

  /**
   * Get enhanced profile data (photo and bio) for a guide.
   * For authenticated users, fetches directly from the database.
   * For unauthenticated users, uses the guide-profile-photo edge function
   * which verifies there's a valid match before returning data.
   */
  static async getGuideEnhancedProfile(guideId: string): Promise<GuideEnhancedProfile | null> {
    const supabase = getSupabaseClient();

    // Check if user is authenticated
    const { data: { session } } = await supabase.auth.getSession();

    if (session?.user) {
      // Authenticated user - fetch directly from database
      const { data, error } = await supabase
        .from('guides_enhanced_profile')
        .select('guide_id, about, image_url, display_name')
        .eq('guide_id', guideId)
        .single();

      if (error) {
        console.error('Failed to fetch enhanced profile:', error.message);
        return null;
      }

      if (!data) {
        return null;
      }

      const profile = data as any;
      return {
        guide_id: profile.guide_id,
        about: profile.about,
        image_url: profile.image_url,
        display_name: profile.display_name,
      };
    }

    // Unauthenticated user - use edge function with seeker_id verification
    const seekerId = this.getSeekerIdFromSession();
    if (!seekerId) {
      console.error('No seeker ID found for unauthenticated profile fetch');
      return null;
    }

    try {
      const { data, error } = await supabase.functions.invoke('guide-profile-photo', {
        body: {
          seeker_id: seekerId,
          guide_id: guideId,
        },
      });

      if (error) {
        console.error('Failed to fetch guide profile photo via edge function:', error.message);
        return null;
      }

      if (!data) {
        return null;
      }

      return {
        guide_id: guideId,
        about: data.about ?? undefined,
        image_url: data.image_url ?? undefined,
        display_name: data.display_name ?? undefined,
      };
    } catch (err) {
      console.error('Error calling guide-profile-photo edge function:', err);
      return null;
    }
  }
}
